import React from "react";

import { CheckMark, Svg, TooltipIcon, legacyTooltipAttrs } from "../svg";

/**
 * Native port of ``cms/blocks/hc-block/hc-block--compare-breeze.html`` (#33404).
 *
 * Class-driven structural compare table; imagery is CSS background-image keyed
 * off element classes. The ``__react_component_tooltip`` static markup is
 * reproduced VERBATIM (a global JS tooltip lib rebinds to it). The duplicate
 * ``id="tooltip-0"`` across every tooltip is intentional legacy markup. The
 * legacy ``{% include 'cms/components/svg.html' %}`` includes become the shared
 * ``Svg``/``TooltipIcon``/``CheckMark`` inline components.
 */

const Tooltip = (props: { text: string }): React.JSX.Element => (
    <>
        <span data-tip="true" data-for="tooltip-0" {...legacyTooltipAttrs}>
            <Svg
                Icon={TooltipIcon}
                src="tooltip-icon"
                className="tooltip__handle"
            />
        </span>
        <div
            className="__react_component_tooltip place-top type-dark tooltip__content"
            id="tooltip-0"
            role="tooltip"
            data-id="tooltip"
            style={{ maxWidth: "275px" }}
        >
            {props.text}
        </div>
    </>
);

export const CompareBreeze = (): React.JSX.Element => (
    <div className="l-capped-width">
        <header>
            <h2>
                Which TEMPUR-Breeze<sup>®</sup> is Right for You?
            </h2>
            <p>All night cooling for deeper, more refreshing sleep.</p>
        </header>
        <div>
            <table>
                <thead>
                    <tr>
                        <th></th>
                        <th>Cooling</th>
                        <th>
                            Cooling
                            <br />
                            Technology
                        </th>
                        <th>
                            <span>
                                Ventilated Advanced
                                <br />
                                Relief&trade; Material{" "}
                                <span
                                    data-tooltip="Our one-of-a-kind, pressure-relieving material features ventilation for increased airflow."
                                    title="Our one-of-a-kind, pressure-relieving material features ventilation for increased airflow."
                                >
                                    <Tooltip text="Our one-of-a-kind, pressure-relieving material features ventilation for increased airflow." />
                                </span>
                            </span>
                        </th>
                        <th>Profile</th>
                        <th>
                            Available
                            <br />
                            Feels
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>
                            <span>
                                ProBreeze<sup>®</sup>
                            </span>
                        </td>
                        <td className="cooling-number">
                            Up to
                            <br />
                            <strong className="cooling-number">5°</strong>
                            <br />
                            <span>
                                cooler<sup>++</sup>
                            </span>
                        </td>
                        <td>
                            <span>
                                Pure Cool<sup>®</sup>
                                <br />
                                Material{" "}
                                <span
                                    data-tooltip="Designed to pull heat away from your body to help you feel cooler as you sleep."
                                    title="Designed to pull heat away from your body to help you feel cooler as you sleep."
                                >
                                    <Tooltip text="Designed to pull heat away from your body to help you feel cooler as you sleep." />
                                </span>
                            </span>
                        </td>
                        <td>
                            <Svg
                                Icon={CheckMark}
                                src="check-mark"
                                className="check-mark"
                            />
                            <div className="blank-space">
                                <br />
                                &nbsp;&nbsp;&nbsp;
                                <br />
                                &nbsp;&nbsp;&nbsp;
                            </div>
                        </td>
                        <td className="profile-height">
                            <strong>
                                12<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>
                            <span>Medium</span>
                            <div>
                                Medium Hybrid{" "}
                                <span
                                    data-tooltip="Over 1000 premium innersprings help deliver the perfect combination of pressure relief and support."
                                    title="Over 1000 premium innersprings help deliver the perfect combination of pressure relief and support."
                                >
                                    <Tooltip text="Over 1000 premium innersprings help deliver the perfect combination of pressure relief and support." />
                                </span>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <span>
                                LuxeBreeze<sup>®</sup>
                            </span>
                        </td>
                        <td>
                            Up to
                            <br />
                            <strong className="cooling-number">10°</strong>
                            <br />
                            <span>
                                cooler<sup>++</sup>
                            </span>
                        </td>
                        <td>
                            <span>
                                <span>
                                    Pure Cool<sup>®</sup> Plus
                                </span>
                                <br />
                                Material{" "}
                                <span
                                    data-tooltip="Our most advanced cooling technology — so even the hottest of sleepers can stay comfortable all night long."
                                    title="Our most advanced cooling technology — so even the hottest of sleepers can stay comfortable all night long."
                                >
                                    <Tooltip text="Our most advanced cooling technology — so even the hottest of sleepers can stay comfortable all night long." />
                                </span>
                            </span>
                        </td>
                        <td>
                            <Svg
                                Icon={CheckMark}
                                src="check-mark"
                                className="check-mark"
                            />
                            <br />+ up to 33% more
                            <br />
                            <span>
                                Tempur-Material<sup>™</sup>
                            </span>
                        </td>
                        <td className="profile-height">
                            <strong>
                                13<sup>&quot;</sup>
                            </strong>
                        </td>
                        <td>
                            Soft
                            <div>
                                Medium Hybrid{" "}
                                <span
                                    data-tooltip="TEMPUR-Material™ plus more than 1,300 premium, double-stacked innersprings provide a lighter, more responsive feel and easier movement in bed."
                                    title="TEMPUR-Material™ plus more than 1,300 premium, double-stacked innersprings provide a lighter, more responsive feel and easier movement in bed."
                                >
                                    <Tooltip text="TEMPUR-Material™ plus more than 1,300 premium, double-stacked innersprings provide a lighter, more responsive feel and easier movement in bed." />
                                </span>
                            </div>
                            <div>Firm</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
);
